import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemIcon from '@mui/material/ListItemIcon';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TableContainer from '@mui/material/TableContainer';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { PresentationErrorCodes, PresentationExportFormat, PresentationTemplateSummary } from 'src/services/presentations/presentations.types';
import {
  useGetPresentationTemplatesQuery,
  useDeletePresentationTemplateMutation,
  useUpdatePresentationTemplateMutation,
  useDuplicatePresentationTemplateMutation,
} from 'src/services/presentations/presentations.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import WordTemplateDialog from '../modals/word-template-dialog';
import { downloadTestRender } from '../hooks/download-test-render';
import TemplateClientDialog from '../modals/template-client-dialog';
import EmployerReplacementsCard from './employer-replacements-card';
import { usePresentationCapabilities } from '../hooks/use-presentation-capabilities';

type NameDialog = { mode: 'copy' | 'rename'; template: PresentationTemplateSummary };

/**
 * The agency's presentation templates (JT1-1886), on the Brand settings tab. System templates can be
 * copied; the agency's own can be renamed, made the default or deleted. A template's sections and
 * design are changed in the builder and saved back as a template.
 */
export default function PresentationTemplatesCard() {
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();
  // Only agencies with the builder see the card, so the settings page can always render it.
  if (tenantType !== TenantType.Recruiter || !featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED)) {
    return null;
  }
  return <TemplatesCard />;
}

function TemplatesCard() {
  const { t } = useTranslate();
  const { account } = useAuthContext();
  const canManage = !!account && [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  const { currentData: templates = [], isFetching } = useGetPresentationTemplatesQuery();
  const [update] = useUpdatePresentationTemplateMutation();
  const [remove, { isLoading: deleting }] = useDeletePresentationTemplateMutation();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; template: PresentationTemplateSummary } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState<{ key: string; name: string } | null>(null);
  const [choosingClient, setChoosingClient] = useState<PresentationTemplateSummary | null>(null);
  const { wordConversion } = usePresentationCapabilities();

  const testRender = async (template: PresentationTemplateSummary, format: PresentationExportFormat) => {
    setMenu(null);
    try {
      await downloadTestRender(template.key, format);
    } catch (error) {
      const code = (error as { data?: { error_code?: string } })?.data?.error_code;
      enqueueSnackbar(t(code === PresentationErrorCodes.ConverterUnavailable ? 'presentations.word.no_converter_short' : 'presentations.word.test_failed'), { variant: 'error' });
    }
  };
  const [naming, setNaming] = useState<NameDialog | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PresentationTemplateSummary | null>(null);

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      enqueueSnackbar(t(done), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  const makeDefault = (template: PresentationTemplateSummary) => {
    setMenu(null);
    run(() => update({ id: template.key, is_default: true }).unwrap(), 'presentations.templates.made_default');
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await run(() => remove(confirmDelete.key).unwrap(), 'presentations.templates.deleted');
    setConfirmDelete(null);
  };

  return (
    <>
    <Card>
      <CardHeader
        title={t('presentations.templates.title')}
        subheader={t('presentations.templates.subheader')}
        action={canManage && (
          <Button variant="outlined" color="inherit" size="small" startIcon={<Iconify icon="solar:document-add-bold" />} onClick={() => setUploading(true)}>
            {t('presentations.word.upload')}
          </Button>
        )}
      />
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('presentations.templates.columns.name')}</TableCell>
              <TableCell>{t('presentations.templates.columns.type')}</TableCell>
              <TableCell>{t('presentations.templates.columns.source')}</TableCell>
              {canManage && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.key} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{template.name}</Typography>
                    {template.is_default && <Label variant="soft" color="primary">{t('presentations.templates.default')}</Label>}
                    {template.format === 'word' && <Label variant="soft" color="info" startIcon={<Iconify icon="solar:document-text-bold" />}>{t('presentations.word.label')}</Label>}
                    {template.format === 'word' && !!template.revision && template.revision > 1 && <Label variant="soft">{t('presentations.word.version', { n: template.revision })}</Label>}
                    {template.anonymise_by_default && <Label variant="soft">{t('presentations.list.anonymised')}</Label>}
                    {template.client_company && <Label variant="soft" color="secondary" startIcon={<Iconify icon="solar:buildings-2-bold" />}>{template.client_company.name}</Label>}
                  </Stack>
                  {template.description && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{template.description}</Typography>}
                </TableCell>
                <TableCell>{t(`presentations.kinds.${template.kind}.label`)}</TableCell>
                <TableCell>{t(`presentations.templates.sources.${template.source}`)}</TableCell>
                {canManage && (
                  <TableCell align="right">
                    <IconButton aria-label={t('presentations.templates.more', { name: template.name })} onClick={(event) => setMenu({ anchor: event.currentTarget, template })}>
                      <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!templates.length && !isFetching && (
              <TableRow><TableCell colSpan={4}>{t('presentations.templates.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        {menu?.template.format === 'word' && (
          <MenuItem disabled={!wordConversion} onClick={() => testRender(menu.template, PresentationExportFormat.PDF)}>
            <ListItemIcon><Iconify icon="solar:file-text-bold" /></ListItemIcon>
            {t('presentations.word.test_pdf')}
          </MenuItem>
        )}
        {menu?.template.format === 'word' && (
          <MenuItem onClick={() => { setReplacing({ key: menu.template.key, name: menu.template.name }); setMenu(null); }}>
            <ListItemIcon><Iconify icon="solar:upload-bold" /></ListItemIcon>
            {t('presentations.word.new_version')}
          </MenuItem>
        )}
        {menu?.template.format === 'word' && (
          <MenuItem onClick={() => testRender(menu.template, PresentationExportFormat.DOCX)}>
            <ListItemIcon><Iconify icon="solar:document-text-bold" /></ListItemIcon>
            {t('presentations.word.test_word')}
          </MenuItem>
        )}
        {menu?.template.source === 'agency' && !menu.template.is_default && (
          <MenuItem onClick={() => makeDefault(menu.template)}>
            <ListItemIcon><Iconify icon="solar:star-bold" /></ListItemIcon>
            {t('presentations.templates.make_default')}
          </MenuItem>
        )}
        {menu?.template.source === 'agency' && (
          <MenuItem onClick={() => { setChoosingClient(menu.template); setMenu(null); }}>
            <ListItemIcon><Iconify icon="solar:buildings-2-bold" /></ListItemIcon>
            {t(menu.template.client_company ? 'presentations.templates.client_change' : 'presentations.templates.client_set_action')}
          </MenuItem>
        )}
        {menu?.template.source === 'agency' && (
          <MenuItem onClick={() => { setNaming({ mode: 'rename', template: menu.template }); setMenu(null); }}>
            <ListItemIcon><Iconify icon="solar:pen-bold" /></ListItemIcon>
            {t('presentations.templates.rename')}
          </MenuItem>
        )}
        <MenuItem onClick={() => { if (menu) setNaming({ mode: 'copy', template: menu.template }); setMenu(null); }}>
          <ListItemIcon><Iconify icon="solar:copy-bold" /></ListItemIcon>
          {t('presentations.templates.copy')}
        </MenuItem>
        {menu?.template.source === 'agency' && (
          <MenuItem sx={{ color: 'error.main' }} onClick={() => { setConfirmDelete(menu.template); setMenu(null); }}>
            <ListItemIcon><Iconify icon="solar:trash-bin-trash-bold" sx={{ color: 'error.main' }} /></ListItemIcon>
            {t('common.delete')}
          </MenuItem>
        )}
      </Menu>

      {naming && <TemplateNameDialog {...naming} onClose={() => setNaming(null)} />}
      {choosingClient && <TemplateClientDialog template={choosingClient} onClose={() => setChoosingClient(null)} />}
      {uploading && <WordTemplateDialog open onClose={() => setUploading(false)} wordConversion={wordConversion} />}
      {replacing && <WordTemplateDialog open onClose={() => setReplacing(null)} wordConversion={wordConversion} replacing={replacing} />}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={t('presentations.templates.delete_title')}
        content={t('presentations.templates.delete_body')}
        action={<Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}>{t('common.delete')}</Button>}
      />
    </Card>
    <EmployerReplacementsCard canManage={canManage} />
    </>
  );
}

function TemplateNameDialog({ mode, template, onClose }: NameDialog & { onClose: VoidFunction }) {
  const { t } = useTranslate();
  const [name, setName] = useState(mode === 'copy' ? t('presentations.templates.copy_name', { name: template.name }) : template.name);
  const [isDefault, setIsDefault] = useState(false);
  const [duplicate, { isLoading: copying }] = useDuplicatePresentationTemplateMutation();
  const [update, { isLoading: renaming }] = useUpdatePresentationTemplateMutation();

  const submit = async () => {
    try {
      if (mode === 'copy') {
        await duplicate({ key: template.key, name: name.trim(), is_default: isDefault }).unwrap();
      } else {
        await update({ id: template.key, name: name.trim() }).unwrap();
      }
      enqueueSnackbar(t(mode === 'copy' ? 'presentations.templates.copied' : 'presentations.templates.renamed'), { variant: 'success' });
      onClose();
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t(mode === 'copy' ? 'presentations.templates.copy_title' : 'presentations.templates.rename_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField autoFocus label={t('presentations.template_dialog.name')} value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 200 }} />
          {mode === 'copy' && (
            <FormControlLabel control={<Checkbox checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />} label={t('presentations.template_dialog.make_default')} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={copying || renaming} disabled={!name.trim()} onClick={submit}>
          {t(mode === 'copy' ? 'presentations.templates.copy' : 'presentations.templates.rename')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
